import type { FieldExplainerContent } from './types';

export type RetirementGoalExplainerContext = {
  input: {
    retirementGoal: number;
    currentSavings: number;
    currentAge: number;
    retirementAge: number;
    expectedReturn: number;
  };
  result: {
    requiredMonthlySavings: number;
    projectedFromCurrent: number;
    yearsToRetirement: number;
    finalBalance: number;
  };
};

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

const usdCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const ASSUMED_INFLATION = 0.03;

export function getRetirementGoalFieldExplainers(ctx: RetirementGoalExplainerContext): FieldExplainerContent[] {
  const { retirementGoal, currentSavings, currentAge, retirementAge, expectedReturn } = ctx.input;
  const { requiredMonthlySavings, projectedFromCurrent, yearsToRetirement, finalBalance } = ctx.result;

  const months = yearsToRetirement * 12;
  const monthlyRate = expectedReturn / 100 / 12;
  const gap = retirementGoal - projectedFromCurrent;
  const fundingPercent = retirementGoal > 0 ? (projectedFromCurrent / retirementGoal) * 100 : 0;
  const todaysDollars = retirementGoal / Math.pow(1 + ASSUMED_INFLATION, yearsToRetirement);
  const rateExpression = `(1 + ${(expectedReturn / 100).toString()}/12)^${months}`;

  return [
    {
      id: 'retirementGoal',
      kind: 'input',
      label: 'Retirement target',
      plain:
        'The finish line. It is not calculated from anything else on this page; every other number is measured against it.',
      formula: `target = ${usd.format(retirementGoal)}   (your input)`,
      feeds: 'The dashed goal line on the chart, the milestone table, and the shortfall figure.',
      assumption: `This is a future-dollar amount. At ${ASSUMED_INFLATION * 100}% inflation, ${usd.format(retirementGoal)} in ${yearsToRetirement} years buys about what ${usd.format(todaysDollars)} buys today.`
    },
    {
      id: 'currentSavings',
      kind: 'input',
      label: 'Current savings',
      plain:
        'Your starting balance. It grows at the expected return for the whole timeline, and whatever it becomes is subtracted from your target before the calculator solves for monthly savings.',
      formula: `${usd.format(currentSavings)} × ${rateExpression}\n  = ${usd.format(projectedFromCurrent)}`,
      feeds: 'The "Projected from current balance" card, which shrinks the gap that required monthly savings must close.'
    },
    {
      id: 'currentAge',
      kind: 'input',
      label: 'Current age',
      plain:
        'Sets the start of the timeline. It is used only to measure the distance to your retirement age; no age-specific tax or withdrawal rules are applied.',
      formula: `years = ${retirementAge} − ${currentAge} = ${yearsToRetirement}`,
      feeds: "The compounding horizon and the chart's age axis.",
      assumption:
        'Nothing here models the 59½ early-withdrawal penalty, Social Security timing, or required minimum distributions. Age is arithmetic, not tax logic.'
    },
    {
      id: 'retirementAge',
      kind: 'input',
      label: 'Retirement age',
      plain:
        'The year the accumulation math stops. This calculator models saving toward the target only; it does not model spending it down in retirement.',
      formula: `months = (${retirementAge} − ${currentAge}) × 12 = ${months}`,
      feeds: 'Every compounding exponent on the page and the last row of the projection table.',
      assumption:
        'Retiring later helps twice: more contribution months, and more compounding on everything already saved. Even one year changes the requirement noticeably.'
    },
    {
      id: 'expectedReturn',
      kind: 'input',
      label: 'Expected return',
      plain:
        'One nominal annual growth rate applied to everything for the whole timeline. It does double duty: it grows your current savings and it compounds every future contribution.',
      formula: `r = ${expectedReturn}% / 12 = ${monthlyRate.toFixed(6)} per month\napplied to current savings\nand to each monthly contribution`,
      feeds: 'Every result card and every line on the chart. It is the most sensitive input on this page.',
      assumption:
        'Assumes the same return every single year: no volatility, no fees, no taxes. Results are nominal, not inflation-adjusted.'
    },
    {
      id: 'requiredMonthlySavings',
      kind: 'result',
      label: 'Required monthly savings',
      plain:
        'The contribution that closes the gap between what your current savings will grow into and your target, solved backward with a standard future-value annuity formula.',
      formula:
        gap > 0
          ? monthlyRate > 0
            ? `gap = ${usd.format(retirementGoal)} − ${usd.format(projectedFromCurrent)}\n    = ${usd.format(gap)}\n\npmt = gap × r / ((1 + r)^${months} − 1)\n    = ${usdCents.format(requiredMonthlySavings)}`
            : `gap = ${usd.format(retirementGoal)} − ${usd.format(projectedFromCurrent)}\n    = ${usd.format(gap)}\n\nat a 0% return nothing compounds,\npmt = gap / ${months} months\n    = ${usdCents.format(requiredMonthlySavings)}`
          : `gap = ${usd.format(retirementGoal)} − ${usd.format(projectedFromCurrent)} ≤ 0\ncurrent savings alone reach the target,\nso no new savings are required`,
      feeds: 'The contributions line on the chart and every row of the projection table.',
      assumption: 'Contributions are assumed to arrive at the end of every month, unchanged for the entire timeline.'
    },
    {
      id: 'projectedFromCurrent',
      kind: 'result',
      label: 'Projected from current balance',
      plain: `Where your current savings land on their own by age ${retirementAge} if you never add another dollar. The percentage compares that to your target.`,
      formula: `${usd.format(currentSavings)} × ${rateExpression}\n  = ${usd.format(projectedFromCurrent)}\n${usd.format(projectedFromCurrent)} / ${usd.format(retirementGoal)} = ${fundingPercent.toFixed(1)}%`,
      feeds: 'It sets the gap that required monthly savings must fill.'
    },
    {
      id: 'shortfall',
      kind: 'result',
      label: 'Shortfall at retirement',
      plain:
        'The final projected balance minus your target. When you contribute the required monthly amount, this reads zero: the plan lands on target by construction.',
      formula: `${usd.format(finalBalance)} − ${usd.format(retirementGoal)}\n  = ${usd.format(finalBalance - retirementGoal)}`,
      feeds: 'Nothing downstream; it is a check figure on the plan.'
    },
    {
      id: 'yearsToRetirement',
      kind: 'result',
      label: 'Years to retirement',
      plain: 'Your two age inputs, subtracted. Shown on its own because it drives every compounding exponent on the page.',
      formula: `max(1, ${retirementAge} − ${currentAge}) = ${yearsToRetirement}`,
      feeds: 'The exponent in every growth calculation here.'
    }
  ];
}
